import emailjs from '@emailjs/browser';

interface ConsultationFormData {
  name: string;
  email: string;
  phone: string;
  company?: string;
  companyType?: string;
  services: string[];
  budget?: string;
  timeline?: string;
  description?: string;
  preferredDate?: string;
}

/**
 * Send consultation form data via EmailJS
 * 
 * To set up EmailJS:
 * 1. Create account at https://www.emailjs.com/
 * 2. Add email service (Gmail, Outlook, etc.)
 * 3. Create email template with these variables:
 *    - {{to_name}}, {{from_name}}, {{email}}, {{phone}}, {{company}}, 
 *      {{company_type}}, {{services}}, {{budget}}, {{timeline}}, 
 *      {{preferred_date}}, {{message}}
 * 4. Get your Public Key, Service ID, and Template ID
 * 5. Add them to .env file:
 *    - VITE_EMAILJS_PUBLIC_KEY
 *    - VITE_EMAILJS_SERVICE_ID
 *    - VITE_EMAILJS_TEMPLATE_ID
 */
export const sendConsultationEmail = async (data: ConsultationFormData): Promise<{ success: boolean; message: string }> => {
  try {
    const publicKeys = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;

    if (!publicKeys || !serviceId || !templateId) {
      console.warn('EmailJS not configured. Add VITE_EMAILJS_PUBLIC_KEY, VITE_EMAILJS_SERVICE_ID, and VITE_EMAILJS_TEMPLATE_ID to .env file');
      // For demo/testing purposes, simulate success
      return {
        success: true,
        message: 'Permintaan konsultasi berhasil dikirim (demo mode)',
      };
    }

    // Initialize EmailJS
    emailjs.init(publicKeys);

    // Format services as comma-separated string
    const servicesList = data.services.join(', ');

    // Send email
    const result = await emailjs.send(serviceId, templateId, {
      to_name: 'Naincode Team',
      from_name: data.name,
      from_email: data.email,
      phone: data.phone,
      company: data.company || '-',
      company_type: data.companyType || '-',
      services: servicesList,
      budget: data.budget || '-',
      timeline: data.timeline || '-',
      preferred_date: data.preferredDate || '-',
      message: data.description || '-',
    });

    if (result.status === 200) {
      return {
        success: true,
        message: 'Permintaan konsultasi berhasil dikirim',
      };
    } else {
      throw new Error('Failed to send email');
    }
  } catch (error) {
    console.error('EmailJS error:', error);
    return {
      success: false,
      message: 'Terjadi kesalahan saat mengirim form',
    };
  }
};
